'use client';

import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

export interface BottomMenuItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href?: string;
  onClick?: () => void;
  isActive?: boolean;
}

export interface CenterAction {
  icon: React.ComponentType<{ className?: string }>;
  label?: string;
  onClick: () => void;
}

interface BottomMobileMenuProps {
  items: BottomMenuItem[];
  variant?: 'default' | 'rounded-cta';
  centerAction?: CenterAction;
  showLabels?: boolean;
  className?: string;
  /** Shift the rounded menu left to make room for the AI orb on the right. */
  shiftLeftForOrb?: boolean;
}

export default function BottomMobileMenu({
  items,
  variant = 'default',
  centerAction,
  showLabels = true,
  className = '',
  shiftLeftForOrb = false,
}: BottomMobileMenuProps) {
  const { darkMode } = useDarkMode();
  const pathname = usePathname();

  // Determine if item is active based on href matching current pathname
  const isItemActive = (item: BottomMenuItem) => {
    if (item.isActive !== undefined) return item.isActive;
    if (item.href) return pathname === item.href;
    return false;
  };

  const renderButton = (item: BottomMenuItem, index: number) => {
    const Icon = item.icon;
    const isActive = isItemActive(item);

    const buttonClasses = `
      inline-flex items-center justify-center px-5 transition-colors
      ${showLabels ? 'flex-col' : ''}
      ${variant === 'rounded-cta' && index === 0 ? 'rounded-s-full' : ''}
      ${variant === 'rounded-cta' && index === items.length - 1 ? 'rounded-e-full' : ''}
      ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}
      group
    `;

    const iconClasses = `
      size-6 transition-colors
      ${showLabels ? 'mb-1' : ''}
      ${
        isActive
          ? 'text-blue-500'
          : darkMode
            ? 'text-gray-400 group-hover:text-blue-500'
            : 'text-gray-500 group-hover:text-blue-600'
      }
    `;

    const labelClasses = `
      text-xs transition-colors
      ${showLabels ? '' : 'sr-only'}
      ${
        isActive
          ? 'text-blue-500'
          : darkMode
            ? 'text-gray-400 group-hover:text-blue-500'
            : 'text-gray-500 group-hover:text-blue-600'
      }
    `;

    const content = (
      <>
        <Icon className={iconClasses} aria-hidden="true" />
        <span className={labelClasses}>{item.label}</span>
      </>
    );

    if (item.href) {
      return (
        <Link key={index} href={item.href} className={buttonClasses}>
          {content}
        </Link>
      );
    }

    return (
      <button key={index} type="button" onClick={item.onClick} className={buttonClasses}>
        {content}
      </button>
    );
  };

  const renderCenterAction = () => {
    if (!centerAction) return null;

    const Icon = centerAction.icon;

    return (
      <div className="flex items-center justify-center">
        <button
          type="button"
          onClick={centerAction.onClick}
          className="inline-flex size-8 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 focus:outline-none dark:focus:ring-blue-800"
          aria-label={centerAction.label || 'Action'}
        >
          <Icon className="size-6" aria-hidden="true" />
          {centerAction.label && <span className="sr-only">{centerAction.label}</span>}
        </button>
      </div>
    );
  };

  // Variant-specific styling
  const containerClasses =
    variant === 'rounded-cta'
      ? `fixed z-50 h-16 max-w-md rounded-full bottom-4 border ${
          shiftLeftForOrb
            ? 'left-4 w-[calc(100%-6.5rem)]' // leaves ~88px on the right for the AI orb
            : 'left-1/2 w-[calc(100%-5rem)] -translate-x-1/2'
        } ${
          darkMode ? 'bg-gray-800/70 border-gray-700' : 'bg-blue-50/70 border-gray-200'
        } backdrop-blur-md ${className}`
      : `fixed bottom-0 left-0 z-50 w-full h-16 border-t ${
          darkMode ? 'bg-gray-900/70 border-gray-700' : 'bg-blue-50/70 border-gray-200'
        } backdrop-blur-md ${className}`;

  // Determine grid columns based on variant, centerAction, and number of items
  const gridCols =
    variant === 'rounded-cta' && centerAction
      ? 'grid-cols-5' // 2 items + center + 2 items
      : items.length === 5
        ? 'grid-cols-5' // 5 items without center
        : 'grid-cols-4'; // 4 items or default

  // Split items for center action variant
  let leftItems = items;
  let rightItems: BottomMenuItem[] = [];

  if (variant === 'rounded-cta' && centerAction) {
    const midPoint = Math.floor(items.length / 2);
    leftItems = items.slice(0, midPoint);
    rightItems = items.slice(midPoint);
  }

  return (
    <div className={containerClasses}>
      <div className={`mx-auto grid h-full max-w-lg font-medium ${gridCols}`}>
        {variant === 'rounded-cta' && centerAction ? (
          <>
            {leftItems.map((item, index) => renderButton(item, index))}
            {renderCenterAction()}
            {rightItems.map((item, index) => renderButton(item, index + leftItems.length))}
          </>
        ) : (
          items.map((item, index) => renderButton(item, index))
        )}
      </div>
    </div>
  );
}
